/**
 * APTO stock lookup helpers.
 *
 * Egg-types whose first FIFO box sits in the APTO freshness window are
 * sold at a discount. The discount is applied to the WHOLE order line,
 * but the discounted bucket holds a finite number of units — once
 * drained, the allocator falls through to FRESCO stock. Without a UI
 * cap, the customer could buy 100 cartons at APTO price while only 15
 * fit in the bucket, and we'd ship 85 FRESCO cartons at the discounted
 * rate (losing the margin spread).
 *
 * This module wraps the public `/api/egg-types/:id/apto-stock` BFF
 * route so the cart + product card can read the bucket count without
 * re-implementing the fetch + error handling.
 */

export type FreshnessState = "FRESCO" | "APTO" | "EXPIRED";

export interface AptoStock {
  eggTypeId: string;
  /** Units available at the APTO discounted price AFTER subtracting soft
   *  reservations held by OTHER active carts. This is the number the UI
   *  should drive the cap from — it already accounts for what other
   *  shoppers have in flight. */
  aptoUnits: number;
  /** Raw APTO bucket size before reservation subtraction. Operator-
   *  facing only — the customer surface shouldn't expose it. Optional
   *  for backwards-compat with older API versions that didn't return
   *  it. */
  physicalAptoUnits?: number;
  /** Units soft-held by OTHER active carts inside the TTL window.
   *  Optional for backwards-compat. */
  reservedAptoUnits?: number;
  /** Units available at the FRESCO regular price. */
  freshUnits: number;
  /** Freshness state of the first FIFO box — the one whose state drives
   *  the active price for this egg-type. NULL when no live stock. */
  currentState: FreshnessState | null;
}

/**
 * Fetches the APTO stock breakdown for a single egg-type. Returns
 * `null` on any failure (network, 4xx, 5xx, bad JSON) so callers can
 * gracefully fall back to "no cap" UX rather than blocking the cart.
 *
 * The fetch goes through the local BFF proxy (origin-locked) which in
 * turn talks to the API. The short server-side cache (5s) absorbs
 * burst reads from the cart re-rendering as the user clicks +/-.
 *
 * `cartId` — when set, the upstream pricing helper excludes that cart's
 * soft reservation from the count. Pass the current user's own cart id
 * so they don't see their own held units as "unavailable". A missing
 * id falls back to the no-exclusion path, which is correct for callers
 * without a cart yet (e.g. the catalog cap on a product page before
 * the user has added anything).
 */
export async function fetchAptoStock(
  eggTypeId: string,
  cartId?: string | null,
): Promise<AptoStock | null> {
  try {
    const path = cartId
      ? `/api/egg-types/${eggTypeId}/apto-stock?cartId=${encodeURIComponent(cartId)}`
      : `/api/egg-types/${eggTypeId}/apto-stock`;
    const res = await fetch(path, {
      method: "GET",
      cache: "no-store",
    });
    if (!res.ok) return null;
    const data = (await res.json()) as Partial<AptoStock>;
    if (typeof data?.eggTypeId !== "string") return null;
    return {
      eggTypeId: data.eggTypeId,
      aptoUnits: Number(data.aptoUnits ?? 0),
      physicalAptoUnits:
        data.physicalAptoUnits !== undefined
          ? Number(data.physicalAptoUnits)
          : undefined,
      reservedAptoUnits:
        data.reservedAptoUnits !== undefined
          ? Number(data.reservedAptoUnits)
          : undefined,
      freshUnits: Number(data.freshUnits ?? 0),
      currentState:
        (data.currentState as FreshnessState | null | undefined) ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Batches APTO stock lookups for several egg-types in parallel.
 * Returns a map keyed by egg-type id. Missing entries (lookup failed)
 * are omitted — callers decide whether absence means "no cap" or
 * "block the line".
 *
 * `cartId` is forwarded to every lookup so the user's own cart never
 * counts against itself across the batch.
 */
export async function fetchAptoStockBatch(
  eggTypeIds: ReadonlyArray<string>,
  cartId?: string | null,
): Promise<Map<string, AptoStock>> {
  const unique = Array.from(new Set(eggTypeIds));
  const results = await Promise.all(
    unique.map((id) => fetchAptoStock(id, cartId)),
  );
  const map = new Map<string, AptoStock>();
  results.forEach((r, i) => {
    if (r) map.set(unique[i], r);
  });
  return map;
}

/**
 * Returns the maximum carton quantity a cart line is allowed to carry
 * before exceeding the APTO bucket of ANY egg-type it references. When
 * no APTO state is active for any contributing egg-type, returns
 * Infinity (no cap). When the bucket is too small to fit even one
 * carton, returns 0 so the UI can render the line as over-cap.
 *
 *  - `unitsPerCartonByEgg`: how many units of each egg-type fit in ONE
 *    carton of this line. MAYOREO → `{ [eggTypeId]: presentation.capacity }`.
 *    MENUDEO → sum 1 per slot, grouped by egg-type.
 *  - `aptoStockByEgg`: stock map (only entries whose state is APTO
 *    contribute to the cap; FRESCO / EXPIRED / null are ignored).
 */
export function maxCartonsUnderAptoCap(
  unitsPerCartonByEgg: ReadonlyMap<string, number>,
  aptoStockByEgg: ReadonlyMap<string, AptoStock>,
): number {
  let cap = Number.POSITIVE_INFINITY;
  for (const [eggTypeId, unitsPerCarton] of unitsPerCartonByEgg) {
    const stock = aptoStockByEgg.get(eggTypeId);
    if (!stock || stock.currentState !== "APTO") continue;
    const allowedCartons = Math.floor(stock.aptoUnits / unitsPerCarton);
    if (allowedCartons < cap) cap = allowedCartons;
  }
  return cap === Number.POSITIVE_INFINITY ? Number.POSITIVE_INFINITY : cap;
}
